/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 04-Aug-2026
 * @author      : Claude
 * @since       : 1.5.0.0
 * Requirement# : Communication Management Implementation Plan, Phase 5 / PR-22
 *                (Version history endpoint + UI), LLD §7.2/§8.5/§10 (2026-08-04 correction).
 * Purpose      : Read-only Provider Web panel listing every non-deleted version of a template
 *                family (any status), with a two-version body diff reusing ReviewDetail's diff
 *                engine. Opened the same way UsagePanel is — from a row action on TemplateList.tsx
 *                (wiring that row action is not part of this PR; see the class-level note).
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * Comms PR-22    | 04-Aug-2026 | Claude   | Initial creation
 */

import React, { useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

import { AsyncBoundary, useAsyncResource } from './AsyncStates';
import { fetchTemplateVersions } from './MessageTemplateService';
import { formatUtcDateTime, requestTypeLabel, statusColor, statusLabel, TemplateSummary } from './CommunicationTypes';
import { DiffPane } from './ReviewDetail';
import { diffTemplates, summariseDiff } from './templateDiff';

/**
 * READ-ONLY, ALWAYS. No edit, publish, approve, reject, or archive action lives here or is ever
 * added here — this screen exists only to show what happened to a template family over time. Any
 * of those actions belongs on the screen for that workflow (Review Detail, Template List), not
 * here.
 *
 * <p><strong>Provider-side data only.</strong> This calls {@code fetchTemplateVersions}, which
 * hits {@code GET /api/v1/message-templates/{uid}/versions} — the provider-admin endpoint LLD §7.2
 * documents (2026-08-04 correction). It deliberately does NOT call the separate, tenant-visibility
 * -scoped `.../versions` read on `CommunicationTemplateApi` (tenant-api) — that is a different
 * capability with no consumer in this screen.
 *
 * <p><strong>One fetch, not one-per-selected-version.</strong> The response already carries every
 * version's full `body` (it reuses `TemplateSummaryDTO`, the same shape `approve`/`clone`/etc.
 * already return) — selecting two versions to compare is a client-side choice over data already
 * in memory, not a second round trip.
 *
 * <p><strong>Opened from `TemplateList`'s row actions</strong> — the "Version history" icon-button,
 * next to Usage, sets `historyTarget` and renders this. It shipped unreachable for one review cycle
 * (exported and tested, but with no call site anywhere in provider-web), which meant the whole
 * lineage capability plus the `fetchTemplateVersions` client behind it was dead surface that no
 * provider admin could see. Same props and same open/close shape as `UsagePanel`, deliberately.
 */
export interface TemplateVersionHistoryProps {
  /** Any TemplateSummary for the family — only templateMasterUid/name are read from it. */
  template: TemplateSummary;
  onClose: () => void;
}

export const TemplateVersionHistory: React.FC<TemplateVersionHistoryProps> = ({ template, onClose }) => {
  const { accessToken } = useOidcAccessToken();

  const { state, reload } = useAsyncResource<TemplateSummary[]>(
    () => fetchTemplateVersions(template.templateMasterUid, accessToken),
    [template.templateMasterUid, accessToken],
  );

  // Selection is version NUMBERS, not array indices — the list this drives can be re-fetched
  // (reload) and indices are not stable identity for a version.
  const [versionA, setVersionA] = useState<number | null>(null);
  const [versionB, setVersionB] = useState<number | null>(null);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth data-testid="version-history-dialog">
      <DialogTitle>Version History — {template.name}</DialogTitle>
      <DialogContent>
        <AsyncBoundary
          state={state}
          what="Version history"
          onRetry={reload}
          loadingLabel="Loading version history…"
          isEmpty={(versions) => versions.length === 0}
          emptyTitle="No versions found for this template."
        >
          {(versions) => (
            <VersionHistoryContent versions={versions} versionA={versionA} versionB={versionB}
              onSelectA={setVersionA} onSelectB={setVersionB} />
          )}
        </AsyncBoundary>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="version-history-close-button">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Compact date for the table — drops the trailing timezone token so the columns stay narrow and the
// dialog never needs a horizontal scrollbar. Strips both offset zones ("GMT+5:30") and locale
// abbreviations ("EDT"/"PST"/"UTC"); the 3–5 uppercase-letter rule deliberately excludes the 2-letter
// "AM"/"PM" so the time itself is never truncated. Full precision still lives in the Review Thread.
const shortDt = (iso: string | null | undefined): string => {
  const formatted = formatUtcDateTime(iso ?? null);
  return formatted ? formatted.replace(/\s+(?:GMT[+-][\d:]+|UTC|[A-Z]{3,5})$/, '') : '—';
};

const VersionHistoryContent: React.FC<{
  versions: TemplateSummary[];
  versionA: number | null;
  versionB: number | null;
  onSelectA: (version: number) => void;
  onSelectB: (version: number) => void;
}> = ({ versions, versionA, versionB, onSelectA, onSelectB }) => {
  // Defaults compare the two newest versions, OLDER first: A (left pane, "current"-side
  // highlighting) is the second-newest/base, B (right pane, "proposed"-side highlighting) is the
  // newest/target — so the default reading is "what changed going into the latest version",
  // matching diffTemplates(currentBody, proposedBody)'s own before/after semantics. Falls back to
  // the single version, twice, when there is only one. The server already sorts newest-first, and
  // this list is never re-sorted here (same "trust the server's order" convention ReviewQueue.tsx
  // documents for its own oldest-first list).
  const resolvedA = versionA ?? versions[1]?.version ?? versions[0]?.version ?? null;
  const resolvedB = versionB ?? versions[0]?.version ?? null;

  const bodyOf = (version: number | null): string | null =>
    version == null ? null : (versions.find((v) => v.version === version)?.body ?? null);

  const diff = useMemo(
    () => diffTemplates(bodyOf(resolvedA), bodyOf(resolvedB)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolvedA, resolvedB, versions],
  );

  const handleSelect = (setter: (version: number) => void) => (event: SelectChangeEvent<number>) => {
    setter(Number(event.target.value));
  };

  return (
    <Stack spacing={2} data-testid="version-history-content">
      <Table size="small" data-testid="version-history-table">
        <TableHead>
          <TableRow>
            <TableCell>Version</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Origin</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Reviewed</TableCell>
            <TableCell>Published</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {versions.map((v) => (
            <React.Fragment key={v.version}>
              <TableRow
                data-testid={`version-history-row-${v.version}`}
                sx={{ '& > td': { borderBottom: v.reviewNotes ? 'none' : undefined } }}
              >
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>v{v.version}</TableCell>
                <TableCell data-testid={`version-type-${v.version}`} sx={{ whiteSpace: 'nowrap' }}>
                  {requestTypeLabel(v.version)}
                </TableCell>
                <TableCell data-testid={`version-origin-${v.version}`} sx={{ whiteSpace: 'nowrap' }}>
                  {v.submittedByTenant ? `Tenant · ${v.submittedByTenant}` : 'Provider'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={statusLabel(v.status)}
                    color={statusColor(v.status)}
                    data-testid={`version-status-${v.version}`}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{shortDt(v.createdAt)}</TableCell>
                {/* Reviewer identity is an opaque object-id — keep it available on hover rather than
                    spending a whole always-wide column on it (that was the horizontal-scroll cause). */}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <span title={v.reviewedBy ?? undefined}>{shortDt(v.reviewedAt)}</span>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{shortDt(v.publishedAt)}</TableCell>
              </TableRow>
              {v.reviewNotes ? (
                // Full-width note under its version — reads like a review comment and needs no extra
                // column, so the row set stays narrow.
                <TableRow>
                  <TableCell />
                  <TableCell
                    colSpan={6}
                    sx={{ pt: 0, color: 'text.secondary', whiteSpace: 'pre-wrap' }}
                    data-testid={`version-note-${v.version}`}
                  >
                    <strong>Review note:</strong> {v.reviewNotes}
                  </TableCell>
                </TableRow>
              ) : null}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      <Divider />

      <Typography variant="subtitle2">Compare versions</Typography>
      <Stack direction="row" spacing={2}>
        {/* native: renders a real <select>/<option> pair rather than a button + listbox popup,
            so this is directly driveable by userEvent.selectOptions in tests — and no less
            accessible for it. */}
        <Select
          native
          size="small"
          value={resolvedA ?? ''}
          onChange={handleSelect(onSelectA)}
          inputProps={{ 'data-testid': 'version-select-a' }}
        >
          {versions.map((v) => (
            <option key={v.version} value={v.version}>
              v{v.version} — {statusLabel(v.status)}
            </option>
          ))}
        </Select>
        <Select
          native
          size="small"
          value={resolvedB ?? ''}
          onChange={handleSelect(onSelectB)}
          inputProps={{ 'data-testid': 'version-select-b' }}
        >
          {versions.map((v) => (
            <option key={v.version} value={v.version}>
              v{v.version} — {statusLabel(v.status)}
            </option>
          ))}
        </Select>
      </Stack>

      <Typography variant="body2" color="text.secondary" data-testid="version-diff-summary">
        {summariseDiff(diff)}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              v{resolvedA ?? '—'}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <DiffPane runs={diff.runs} side="current" testId="version-diff-pane-a" />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="primary">
              v{resolvedB ?? '—'}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <DiffPane runs={diff.runs} side="proposed" testId="version-diff-pane-b" />
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default TemplateVersionHistory;
